import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useToast } from '@bonitour/components'
import { useParamsFilter } from 'Shared/hooks/useParamsFilter'
import { getBraspagSchedule } from 'Core/io/v1/schedule/getBraspagSchedules'
import { usePagination } from '@bonitour/app-functions'
import { identity } from '@bonitour/common-functions'
import { PER_PAGE } from '../constants/braspagSchedule'

export const useGetBraspagSchedule = () => {
  const { add: addToast } = useToast()
  const [filters, setFilters] = useParamsFilter()
  const [loading, setLoading] = useState(false)
  const [braspagScheduleData, setBraspagScheduleData] = useState([])
  const fetchAlreadyCalled = useRef(false)

  const {
    pagination,
    setPagination,
    handleOnPageChange
  } = usePagination({ perPage: PER_PAGE })

  const braspagScheduleDataPartial = useMemo(
    () => {
      const start = (pagination?.page - 1) * pagination?.perPage
      const end = start + pagination?.perPage

      return braspagScheduleData.slice(start, end)
    }
    , [braspagScheduleData, pagination?.page, pagination?.perPage]
  )

  const totalPrice = useMemo(() => {
    if (loading) return 0

    return braspagScheduleData.reduce(
      (acc, item) => acc + ((item?.forecastedNetAmount || 0) / 100), 0
    )
  }, [braspagScheduleData, loading])

  const fetchBraspagSchedule = useCallback(async (
    {
      newFilters,
      finallyCallback
    }
  ) => {
    setLoading(true)
    setBraspagScheduleData([])

    let pageQty = 1
    let currentPage = 1
    let total = 0
    let allData = []

    while (currentPage <= pageQty) {
      const [res, hasError] = await getBraspagSchedule(
        newFilters || filters, currentPage
      )

      if (hasError) {
        addToast('Erro ao buscar agenda da braspag')
        setLoading(false)
        setBraspagScheduleData([])
        return
      }

      pageQty = res?.meta?.totalPages || 1
      total = res?.meta?.totalEntries || 0
      currentPage += 1

      allData = [...allData, ...(res?.data || [])]
    }

    setLoading(false)
    setBraspagScheduleData(allData)
    setPagination(
      {
        totalItems: total,
        totalPages: Math.ceil(allData.length / PER_PAGE),
        perPage: PER_PAGE,
        page: 1
      }
    )

    finallyCallback && finallyCallback()
  }, [addToast, filters, setPagination])

  const handleFiltersChange = useCallback((newFilters) => {
    fetchAlreadyCalled.current = true
    setFilters(newFilters)
    fetchBraspagSchedule({ newFilters })
  }, [fetchBraspagSchedule, setFilters])

  useEffect(() => {
    if (fetchAlreadyCalled.current) {
      return
    }

    if ((!filters?.initialDate && !filters?.finalDate) || braspagScheduleData?.length) return

    fetchBraspagSchedule({})
    fetchAlreadyCalled.current = false
  }, [
    filters,
    fetchBraspagSchedule,
    braspagScheduleData?.data?.length,
    pagination.perPage,
    braspagScheduleData?.length
  ])

  return {
    filters,
    handleFiltersChange,
    braspagScheduleData: braspagScheduleDataPartial,
    loading,
    handleOnPageChange: handleOnPageChange(identity),
    pagination,
    totalPrice
  }
}
